import { Settings, DateTime, Duration, Interval, Info } from 'luxon';
import type { Ref } from 'vue';
import i18n from '@/plugins/i18n';

Settings.defaultLocale = (i18n.global.locale as unknown as Ref).value;

const parseToLuxon = (input: number | string | Date | DateTime): DateTime => {
  if (typeof input === 'number') {
    return DateTime.fromMillis(input);
  }
  if (typeof input === 'string') {
    if (input.match(/^\/Date\(-?\d+\)\/$/)) {
      return DateTime.fromMillis(
        parseInt(input.replace(/\/Date\((-?\d+)/, '$1'), 10),
      );
    }
    // Matches "2017-05-15", "2017-05-15 09:24:15"
    if (input.match(/^\d{4}-\d{2}-\d{2}( \d{2}:\d{2}:\d{2})?$/)) {
      return DateTime.fromSQL(input);
    }
    return DateTime.fromISO(input);
  }
  if (input instanceof Date) {
    return DateTime.fromJSDate(input);
  }
  return input;
};

type ParseInput = string | number | Date | DateTime;
export type ParseToDateTime = (input: ParseInput) => DateTime;
export type ParseToDate = (input: ParseInput) => Date;

export default function useLuxon(): {
  DateTime: typeof DateTime;
  Duration: typeof Duration;
  Interval: typeof Interval;
  Info: typeof Info;
  parseToDateTime: ParseToDateTime;
  parseToDate: ParseToDate;
  parseToIsoDate: (input: ParseInput) => string;
} {
  const parseToDateTime = (input: ParseInput) => parseToLuxon(input);
  const parseToDate = (input: ParseInput) =>
    input instanceof Date ? input : parseToLuxon(input).toJSDate();
  const parseToIsoDate = (input: ParseInput): string =>
    parseToLuxon(input).toISODate() || '';

  return {
    DateTime,
    Duration,
    Interval,
    Info,
    parseToDate,
    parseToDateTime,
    parseToIsoDate,
  };
}
